import * as React from "react";
import * as AvatarPrimitive from "@radix-ui/react-avatar";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";
import { PERSON_PHOTO_URL_PREFIX, resolvePersonPhotoUrl } from "@/lib/person-photo-url";
import { sanitizeImageUrl } from "@/lib/sanitize";

// ---------------------------------------------------------------------------
// Ring variant for status indication
// ---------------------------------------------------------------------------
const avatarRingVariants = cva(
  // Base: always apply ring-offset so the ring floats away from the avatar
  "ring-offset-2 ring-offset-background",
  {
    variants: {
      ring: {
        none: "",
        default: "ring-2 ring-border/20",
        active: "ring-2 ring-success-400",
        attention: "ring-2 ring-warning-400",
      },
    },
    defaultVariants: {
      ring: "none",
    },
  }
);

// ---------------------------------------------------------------------------
// Avatar (root)
// ---------------------------------------------------------------------------
export interface AvatarProps
  extends React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root>,
    VariantProps<typeof avatarRingVariants> {}

const Avatar = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Root>,
  AvatarProps
>(({ className, ring, ...props }, ref) => (
  <AvatarPrimitive.Root
    ref={ref}
    className={cn(
      "relative flex h-10 w-10 shrink-0 overflow-hidden rounded-full",
      // Hover lift & shadow enhancements
      "shadow-soft transition-transform duration-200 hover:scale-105",
      avatarRingVariants({ ring }),
      className
    )}
    {...props}
  />
));
Avatar.displayName = AvatarPrimitive.Root.displayName;

// ---------------------------------------------------------------------------
// AvatarImage
// ---------------------------------------------------------------------------
const AvatarImage = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Image>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Image>
>(({ className, src, ...props }, ref) => {
  const [resolvedSrc, setResolvedSrc] = React.useState<string | undefined>(() => {
    if (typeof src !== "string") return undefined;
    if (src.startsWith(PERSON_PHOTO_URL_PREFIX)) return undefined;
    return sanitizeImageUrl(src) || undefined;
  });

  React.useEffect(() => {
    let isCurrent = true;

    const nextSrc = typeof src === "string" ? src : undefined;
    if (!nextSrc) {
      setResolvedSrc(undefined);
      return () => {
        isCurrent = false;
      };
    }

    resolvePersonPhotoUrl(nextSrc)
      .then((resolvedUrl) => {
        if (isCurrent) {
          setResolvedSrc(sanitizeImageUrl(resolvedUrl) || undefined);
        }
      })
      .catch(() => {
        if (isCurrent) {
          setResolvedSrc(sanitizeImageUrl(nextSrc) || undefined);
        }
      });

    return () => {
      isCurrent = false;
    };
  }, [src]);

  return (
    <AvatarPrimitive.Image
      ref={ref}
      className={cn(
        "aspect-square h-full w-full",
        // Smooth fade-in when the image loads
        "transition-opacity duration-300",
        className
      )}
      src={resolvedSrc}
      {...props}
    />
  );
});
AvatarImage.displayName = AvatarPrimitive.Image.displayName;

// ---------------------------------------------------------------------------
// AvatarFallback
// ---------------------------------------------------------------------------
const AvatarFallback = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Fallback>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Fallback>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Fallback
    ref={ref}
    className={cn(
      "flex h-full w-full items-center justify-center rounded-full",
      // Gradient background for initials instead of flat muted color
      "bg-gradient-to-br from-forge-navy-light to-forge-navy text-white",
      className
    )}
    {...props}
  />
));
AvatarFallback.displayName = AvatarPrimitive.Fallback.displayName;

// ---------------------------------------------------------------------------
// AvatarGroup – overlapping stack of avatars
// ---------------------------------------------------------------------------
export interface AvatarGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Maximum number of avatars to show before a "+N" indicator */
  max?: number;
}

const AvatarGroup = React.forwardRef<HTMLDivElement, AvatarGroupProps>(
  ({ className, max, children, ...props }, ref) => {
    const childArray = React.Children.toArray(children);
    const visible = max != null ? childArray.slice(0, max) : childArray;
    const overflow = max != null ? childArray.length - max : 0;

    return (
      <div
        ref={ref}
        className={cn("flex items-center -space-x-3", className)}
        {...props}
      >
        {visible.map((child, index) => (
          <div
            key={index}
            className="ring-2 ring-background rounded-full"
            style={{ zIndex: visible.length - index }}
          >
            {child}
          </div>
        ))}
        {overflow > 0 && (
          <div
            className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-medium ring-2 ring-background"
            style={{ zIndex: 0 }}
          >
            +{overflow}
          </div>
        )}
      </div>
    );
  }
);
AvatarGroup.displayName = "AvatarGroup";

export { Avatar, AvatarImage, AvatarFallback, AvatarGroup };
